import { useClasses } from "../../hooks/useClasses";
import React, { useState, useEffect } from "react";
import { FiPlus, FiTrash2, FiUsers } from "react-icons/fi";
import { useAuth } from "../../contexts/AuthContext";
import { useData } from "../../contexts/DataContext";
import Modal from "../../components/shared/Modal";
import { toast } from "react-toastify";
import { format, isPast } from "date-fns";

// Safe date converter (handles Firestore Timestamps)
const toDate = v => { if(!v) return new Date(); if(typeof v?.toDate==="function") return v.toDate(); return new Date(v); };





const SUBJECTS = ["Mathematics","Science","English","Nepali","Social Studies","Computer","Health"];
const COLORS   = ["#054bb4","#059669","#7c3aed","#d97706","#dc2626","#0284c7"];

export default function TeacherGroups() {
  const { userData } = useAuth();
  const { classes } = useClasses();
  const { getGroups, addGroup, deleteGroup, getStudents } = useData();
  const [groups, setGroups] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [selected, setSelected] = useState([]);
  const [errors, setErrors]   = useState({});
  const [form, setForm] = useState({ name:"", subject:"", classId:"", task:"", deadline:"" });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); getStudents().then(setStudents).catch(()=>{}); }, []);
  const load = async () => { setLoading(true); const d = await getGroups({ teacherId: userData?.uid }); setGroups(d); setLoading(false); };

  const validate = () => {
    const e = {};
    if (!form.name.trim())    e.name    = "Required";
    if (!form.subject)        e.subject  = "Required";
    if (!form.classId)        e.classId  = "Required";
    if (!form.task.trim())    e.task     = "Required";
    if (selected.length === 0) e.members = "Add at least one member";
    return e;
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    try {
      await addGroup({ ...form, className: (()=>{ const cl=classes.find(x=>x.id===form.classId); return cl?`${cl.name} - ${cl.section}`:""; })(), teacherId: userData?.uid, teacherName: userData?.name, members: selected.map(s=>s.name), memberCount: selected.length, deadline: form.deadline ? toDate(form.deadline).toISOString() : null });
      toast.success("Group created!"); setShowAdd(false); setForm({name:"",subject:"",classId:"",task:"",deadline:""}); setSelected([]); setErrors({});
      load();
    } catch { toast.error("Failed"); }
    finally { setSaving(false); }
  };

  const toggle = (s) => setSelected(p => p.find(x=>x.id===s.id) ? p.filter(x=>x.id!==s.id) : [...p, s]);
  const delGroup = async (id) => { if (!window.confirm("Delete group?")) return; await deleteGroup(id); toast.success("Deleted!"); load(); };

  return (
    <div className="fade-in">
      <div className="page-head">
        <div><h1 className="page-title">👥 Group Work</h1><p className="page-subtitle">Manage student collaborative groups</p></div>
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}><FiPlus size={15}/>Create Group</button>
      </div>

      {loading ? <div className="loading-center"><div className="spinner"/></div> :
        groups.length === 0 ? (
          <div className="card"><div className="empty-state"><div className="empty-icon">👥</div><div className="empty-title">No groups yet</div><div className="empty-desc">Create groups for collaborative assignments.</div></div></div>
        ) : (
          <div className="g g2">
            {groups.map((g, i) => {
              const clr = COLORS[i % COLORS.length];
              const over = g.deadline && isPast(toDate(g.deadline));
              return (
                <div key={g.id} className="card">
                  <div style={{ height: 3, background: clr }}/>
                  <div style={{ padding: "14px 16px" }}>
                    <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:10 }}>
                      <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                        <div style={{ width:38,height:38,borderRadius:"var(--r8)",background:clr+"15",display:"flex",alignItems:"center",justifyContent:"center" }}>
                          <FiUsers size={17} style={{ color: clr }}/>
                        </div>
                        <div>
                          <h4 style={{ fontSize:".92rem",fontWeight:700 }}>{g.name}</h4>
                          <div style={{ display:"flex",gap:5,marginTop:3 }}>
                            <span className="badge badge-blue" style={{fontSize:".65rem"}}>{g.subject}</span>
                            <span className="badge badge-gray" style={{fontSize:".65rem"}}>{g.memberCount||0} members</span>
                          </div>
                        </div>
                      </div>
                      <button className="btn btn-danger btn-sm btn-icon" onClick={() => delGroup(g.id)}><FiTrash2 size={12}/></button>
                    </div>
                    <div style={{ background:"var(--gray-50)",borderRadius:"var(--r6)",padding:"9px 12px",marginBottom:10,borderLeft:`3px solid ${clr}` }}>
                      <div style={{ fontSize:".68rem",fontWeight:700,color:clr,marginBottom:3 }}>TASK</div>
                      <div style={{ fontSize:".83rem",color:"var(--gray-700)",lineHeight:1.5 }}>{g.task}</div>
                    </div>
                    <div style={{ display:"flex",gap:5,flexWrap:"wrap",marginBottom:8 }}>
                      {(g.members||[]).slice(0,5).map((m,mi)=>(
                        <div key={mi} className="avatar av-sm" style={{ background:`hsl(${mi*70},60%,88%)`,color:`hsl(${mi*70},60%,30%)`,fontSize:".62rem" }}>
                          {(typeof m==="string"?m:(m?.name||"")).split(" ").map(n=>n[0]).join("").slice(0,2)}
                        </div>
                      ))}
                    </div>
                    {g.deadline && <div style={{ fontSize:".74rem",color:over?"var(--red-600)":"var(--amber-600)",fontWeight:600 }}>
                      📅 {over?"Overdue":"Due"}: {format(toDate(g.deadline),"MMM d, yyyy")}
                    </div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      <Modal show={showAdd} onClose={() => {setShowAdd(false);setErrors({});setSelected([]);}} title="Create Group" size="lg"
        footer={<><button className="btn btn-ghost" onClick={()=>setShowAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={handleAdd} disabled={saving}>{saving?<><div className="spinner spinner-sm"/>…</>:"Create"}</button></>}>
        <div className="g g2">
          <div className="form-group">
            <label className="form-label">Group Name *</label>
            <input className={`form-control${errors.name?" error":""}`} placeholder="e.g. Science Team A" value={form.name} onChange={e=>{setForm(p=>({...p,name:e.target.value}));setErrors(p=>({...p,name:""}));}}/>
            {errors.name&&<div className="form-error">{errors.name}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Subject *</label>
            <select className={`form-control${errors.subject?" error":""}`} value={form.subject} onChange={e=>{setForm(p=>({...p,subject:e.target.value}));setErrors(p=>({...p,subject:""}));}}>
              <option value="">Select</option>{SUBJECTS.map(s=><option key={s}>{s}</option>)}
            </select>
            {errors.subject&&<div className="form-error">{errors.subject}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Class *</label>
            <select className={`form-control${errors.classId?" error":""}`} value={form.classId} onChange={e=>{setForm(p=>({...p,classId:e.target.value}));setErrors(p=>({...p,classId:""}));}}>
            </select>
            {errors.classId&&<div className="form-error">{errors.classId}</div>}
          </div>
          <div className="form-group">
            <label className="form-label">Deadline</label>
            <input type="date" className="form-control" value={form.deadline} min={new Date().toISOString().split("T")[0]} onChange={e=>setForm(p=>({...p,deadline:e.target.value}))}/>
          </div>
          <div className="form-group" style={{ gridColumn:"1/-1" }}>
            <label className="form-label">Task Description *</label>
            <textarea className={`form-control${errors.task?" error":""}`} rows={3} placeholder="What should this group do?" value={form.task} onChange={e=>{setForm(p=>({...p,task:e.target.value}));setErrors(p=>({...p,task:""}));}}/>
            {errors.task&&<div className="form-error">{errors.task}</div>}
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Add Members * {selected.length>0&&`(${selected.length} selected)`}</label>
          {errors.members&&<div className="form-error" style={{marginBottom:8}}>{errors.members}</div>}
          <div style={{ display:"flex",flexWrap:"wrap",gap:7 }}>
            {students.map(s => {
              const sel = selected.find(x=>x.id===s.id);
              return (
                <button key={s.id} type="button" onClick={() => toggle(s)} style={{
                  padding:"5px 12px",borderRadius:20,cursor:"pointer",fontSize:".8rem",fontWeight:sel?700:400,
                  border:`1.5px solid ${sel?"var(--blue-700)":"var(--border)"}`,
                  background:sel?"var(--blue-100)":"#fff",color:sel?"var(--blue-700)":"var(--gray-500)",
                  fontFamily:"Inter,sans-serif",transition:"var(--t)",
                }}>
                  {sel && "✓ "}{s.name}
                </button>
              );
            })}
          </div>
        </div>
      </Modal>
    </div>
  );
}
